import React, { FC, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { theme, Text, Row, Space, Icon, Image, Spinner } from 'ui'
import { Button, Radio, Checkbox, IconButton,  Select, InputLabel, MenuItem } from '@material-ui/core'
import { Edit, Delete, Add, Close, ChevronLeft, ChevronRight } from '@material-ui/icons'
import { DateTimePicker, MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers'
import {
  fetchTags,
  fetchTagTypes,
  User
} from './mockUsersApi'
import { saveFullUser } from './saveUserAction'
import { validEmail } from 'utils/validations'
import DateFnsUtils from '@date-io/date-fns'
import DateInputWithCalendar from './CalendarInput' 
import EmergencySection from './Sections/EmergencySection'
import FamilySection from './Sections/FamilySection'

import { TextField } from '@material-ui/core'
import { observer } from 'mobx-react'
import useStore, { snackBar } from 'store'

import Users from 'assets/icons/Users.svg'
import Close1Icon from 'assets/icons/ims/Close1.svg'
import SaveIcom from 'assets/icons/ims/Save.svg'
import ContactInfoSection from './Sections/ContactInfoSection'
import PopulationSection from './Sections/PopulationSection'


const Header = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 23.992px 16px 23.991px;
  flex-direction: column;
  align-items: flex-end;
  border-bottom: 0.909px solid #E2E8F0; 
  background: rgba(241, 245, 249, 0.30);
`
const HContainer = styled.div`
  display: flex;
  height: 39.986px;
  padding-right: 0;  
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
`
const HLeftContainer = styled.div`
  display: flex;
  height: 39.986px;
  align-items: center;
  gap: 7.997px;
`
const HRightContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: #0F172A;
  font-family: Rubik;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
`
const ImageIcon = styled(Image)`
  width: 16px;
  height: 16px;
  stroke-width: 1.333px;
  stroke: #FFF;
`
const Button1 = styled(Button)`
  display: flex;
  padding: 7px 16px;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #2563EB;
  color: white;
`
const Button2 = styled(Button)`
  display: flex;
  padding: 7px 16px;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #F1F5F9;
`
const StyledIconButton = styled(IconButton)`
  display: flex;
  width: 35.994px;
  height: 35.994px;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
`
const StyledClose = styled(Close)`
  color: #000; 
  font-size: 20px;
`
const Body = styled.div`
  display: flex;
  width: 100%;
  height: calc(100vh - 120px);
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
`
const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 31.989px;
  flex-shrink: 0;
  align-self: stretch;
`
const DialogHeader = styled.div`
   max-height:1000px;
  .MuiDialogTitle-root {
   position: fixed;
    display: flex;
    height: 64px;
    background: ${theme.color.dialogBackground};
    align-items: center;
    justify-content: space-between;
    padding: 16px 36px;
  }

  .MuiIconButton-root {
    padding: 0;
  }
`

const AddUserModal = ({
  open,
  onCloseModal,
  disableEdit,
  user,
  oldFamilyRelationIds,
  oldTagRelation,
  onSaved
}) => {
  const { t } = useTranslation('members')
  const { user: userStore, orgs: orgStore, members } = useStore()

  const validateId = (id: string): boolean => {
    if (!/^\d{9}$/.test(id)) return false
    let sum = 0
    for (let i = 0; i < 9; i++) {
      let digit = parseInt(id[i]) * ((i % 2) + 1)
      if (digit > 9) digit -= 9
      sum += digit
    }
    return sum % 10 === 0
  }

  const validatePhone = (phone: string): boolean => {
    const stripped = phone.replace(/-/g, '')
    return /^\+972\d{9}$/.test(stripped) || /^0\d{9}$/.test(stripped)
  }
  const [saving, setSaving] = useState(false)
  const initialFormRef = useRef<typeof form | null>(null)
  const [error, setValidationError] = useState({ population: true })
  const [tagTypes, setTagTypes] = useState([])
  const [tags, setTags] = useState([])

  const [form, setForm] = useState({
    id: 0,
    population: {
      firstName: '',
      lastName: '',
      identityNumber: '',
      city: '',
      street: '',
      houseNumber: '',
      userTypes: [],
      isDead: false,
      birthDate: null,
      isLocalMember: true,
      deathDate: null,
      email: '',
      phone: '',
      roleInCommunity: '',
    },
    emergency: {
      contacts: []
    },
    family: {
      household: null,
      spouse: null,
      children: []
    },
    contacts: {
      roles: [],
      tags: [],
      
      // Hidden      
      groups: [],
      permissions: {},
    },
  })

  useEffect(() => {
    if (user) {
      setForm(user)
      initialFormRef.current = user
      setValidationError({ population: true })
    } else {
      initialFormRef.current = null
    }
  }, [user])

  useEffect(() => {
    let mounted = true

    fetchTags(orgStore.currentOrgId).then(items => {
      setTags(items)
    })

    fetchTagTypes().then(items => {
      setTagTypes(items)
    })
    
    return () => {
      mounted = false
    }
  }, [])

  const setError = (patch) => {
    setValidationError({ ...error, ...patch })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveFullUser(form, orgStore.currentOrgId, {
        originalHousehold: user?.family?.household,
        oldFamilyRelationIds,
        oldTagRelation,
      })
      snackBar.showSuccess('איש הקשר נשמר בהצלחה')
      onCloseModal(false)
      onSaved?.()
      await members.getMembers(orgStore.currentOrgId)
    } catch (e: any) {
      console.error(e)
      snackBar.showError(e?.message || 'שגיאה בשמירת איש הקשר')
    } finally {
      setSaving(false)
    }
  }

  const updateSection = (section: keyof typeof form, value: any) => {
    setForm(prev => ({ ...prev, [section]: value,}))
  }

  return (
    <>
      <Dialog 
        style={{ zIndex: 1200 }} 
        open={open} 
        fullWidth 
        fullScreen
        maxWidth={'lg'}
        scroll={'body'}
      >
        <DialogHeader>
          <Header>
            <HContainer>                    
              <HRightContainer>
                הוספת חבר קהילה חדש   
              </HRightContainer>
              <HLeftContainer>
                {!disableEdit && 
                <>
                  <Button2 onClick={() => onCloseModal(false)} startIcon={<ImageIcon src={Close1Icon}/>}>ביטול</Button2>
                  <Button1
                    onClick={handleSave}
                    disabled={
                      saving ||
                      JSON.stringify(form) === JSON.stringify(initialFormRef.current) ||
                      !form.population.firstName?.trim() ||
                      !form.population.lastName?.trim() ||
                      !validateId(form.population.identityNumber?.trim() || '') ||
                      (!validEmail(form.population.email) && !validatePhone(form.population.phone))
                    }
                    startIcon={saving ? <Spinner size={16} /> : <ImageIcon src={SaveIcom} />} >שמירה
                  </Button1>
                </>}
                <StyledIconButton onClick={() => onCloseModal(false)}><StyledClose/></StyledIconButton>
              </HLeftContainer>
            </HContainer>
          </Header>
          </DialogHeader>
          <DialogContent>
            <Body>
                <BodyContainer>
                    <PopulationSection  
                      value={form.population}
                      onChange={v => updateSection('population', v)}
                      disableEdit={disableEdit}
                      setError={setError}
                      tags={tags}
                      tagTypes={tagTypes.data}
                    />
                    <EmergencySection
                      value={form.emergency}
                      onChange={v => updateSection('emergency', v)}
                      disableEdit={disableEdit}
                      setError={setError}
                    />            
                    <FamilySection
                      value={form.family}
                      onChange={v => updateSection('family', v)}
                      houseNumber={form.population.houseNumber}
                      disableEdit={disableEdit}
                      setError={setError}
                      currentUserId={form.id}
                     />
                    <ContactInfoSection
                      value={form.contacts}
                      onChange={v => updateSection('contacts', v)}
                      disableEdit={disableEdit}
                      setError={setError}
                      tags={tags}
                      tagTypes={tagTypes.data}
                    />
                </BodyContainer>
            </Body>
            </DialogContent>
        </Dialog>
    </>
  )
}

export default observer(AddUserModal)


